import React from 'react';

/* The Design panel of the homepage spine: the invoice workflow that runs in
   the demo org ("Invoice posting — gated over $10k", built from the seeded
   ex-doc-extraction example: scripts/seed-example-pipelines.mjs), drawn as
   one scalable SVG and driven by the shared replay clock. Same block types
   and port names as the console's NqubeStage. Block states are the
   console's: queued · processing (pulsing light) · done · branch not taken
   (dashed). While the for-each extracts, an inset ticks the three invoices
   in — the one over policy is the one the gate holds. */

// start/end = seconds on the spine clock (see SpineRun.js TIMELINE).
const W = 120; const H = 44; const PY = 22;
const cx = (c) => 12 + c * 142;
export const NODES = [
  { id: 'batch', title: 'Invoices', type: 'Input · one per line', color: '#8b7cff', x: cx(0), y: 10, start: 0, end: 0.8, done: '3 invoices' },
  { id: 'extract', title: 'Extract fields', type: 'For-each · LLM per invoice', color: '#8b7cff', x: cx(1), y: 10, start: 0.8, end: 7.5, done: 'vendor · amount · terms ×3' },
  { id: 'over', title: 'Over $10k?', type: 'Branch filter · keyword HOLD', color: '#ffd166', x: cx(2), y: 10, start: 7.5, end: 10.5, done: 'pass · 1 of 3 held' },
  { id: 'review', title: 'A person approves', type: 'Human review', color: '#ffd166', x: cx(3), y: 10, start: 10.5, end: 13.5, done: 'approved' },
  { id: 'post', title: 'Post to ERP', type: 'Webhook · erp-post', color: '#37d99a', x: cx(2), y: 128, start: 16.5, end: 17.5, done: 'committed · receipt' },
];
const byId = Object.fromEntries(NODES.map((n) => [n.id, n]));
const right = (n) => ({ x: n.x + W, y: n.y + PY });
const left = (n) => ({ x: n.x, y: n.y + PY });
const bottom = (n) => ({ x: n.x + W / 2, y: n.y + H });
const top = (n) => ({ x: n.x + W / 2, y: n.y });
const hcurve = (a, b) => { const d = Math.sign(b.x - a.x) * 26; return `M${a.x} ${a.y} C ${a.x + d} ${a.y}, ${b.x - d} ${b.y}, ${b.x} ${b.y}`; };
const EDGES = [
  { id: 'batch-extract', d: hcurve(right(byId.batch), left(byId.extract)), from: 'batch', to: 'extract' },
  { id: 'extract-over', d: hcurve(right(byId.extract), left(byId.over)), from: 'extract', to: 'over' },
  { id: 'over-review', d: hcurve(right(byId.over), left(byId.review)), from: 'over', to: 'review', label: 'pass · held', lx: (right(byId.over).x + left(byId.review).x) / 2, ly: right(byId.over).y - 6, mid: true },
  // the whole batch is under policy → straight to the post (not this run)
  { id: 'over-post', d: `M${bottom(byId.over).x} ${bottom(byId.over).y} L ${top(byId.post).x} ${top(byId.post).y}`, from: 'over', to: 'post', label: 'fail · all under', notTaken: true, lx: bottom(byId.over).x + 6, ly: bottom(byId.over).y + 40 },
  { id: 'review-post', d: `M${bottom(byId.review).x} ${bottom(byId.review).y} C ${bottom(byId.review).x} ${bottom(byId.review).y + 70}, ${right(byId.post).x + 40} ${right(byId.post).y}, ${right(byId.post).x} ${right(byId.post).y}`, from: 'review', to: 'post', label: 'approve', lx: bottom(byId.review).x + 6, ly: bottom(byId.review).y + 44 },
];

export const nodeState = (n, t) => (t < n.start ? 'queued' : t < n.end ? 'running' : 'done');
function edgeState(e, t) {
  const s = nodeState(byId[e.from], t); const d = nodeState(byId[e.to], t);
  if (e.notTaken) return s === 'done' ? 'dead' : 'idle';
  if (s === 'done' && d === 'done') return 'done';
  if (s === 'done') return 'flowing';
  return 'idle';
}

// The three invoices the for-each reads, landing one by one while `extract`
// runs. Same rows as the demo run's input; the third is over policy.
const ROWS = [
  { at: 2.2, id: 'INV-20839', vendor: 'Acme Industrial', amount: '$2,140.00', terms: 'net 30' },
  { at: 4.4, id: 'INV-20840', vendor: 'Globex', amount: '$980.00', terms: 'due on receipt' },
  { at: 6.6, id: 'INV-20841', vendor: 'Northwind Supply', amount: '$12,480.00', terms: 'net 30', hold: true },
];

export default function RunCanvas({ t }) {
  const ex = byId.extract;
  const extracting = t >= ex.start && t < byId.over.end;
  const done = t >= byId.post.end;
  return (
    <div className="rc">
      <div className="rc-bar">
        <strong>Invoice posting — gated over $10k</strong>
        <span className="rc-run"><i className={`nq-light nq-light-${done ? 'done' : 'running'}`} />{done ? 'completed' : 'running'}</span>
        <span className="rc-chip">5 blocks · 1 write</span>
        <span className="rc-chip rc-chip-ok">agentOS ✓</span>
      </div>
      <svg className="rc-svg" viewBox="0 0 560 212" aria-hidden="true">
        {EDGES.map((e) => {
          const st = edgeState(e, t);
          return (
            <g key={e.id}>
              <path d={e.d} className={`rc-edge is-${st}`} />
              {st === 'flowing' && [0, 0.45, 0.9].map((b) => (
                <circle key={b} r="3.5" className="nq-packet"><animateMotion dur="1.35s" begin={`${b}s`} repeatCount="indefinite" path={e.d} /></circle>
              ))}
            </g>
          );
        })}
        {EDGES.filter((e) => e.label).map((e) => (
          <text key={e.id} x={e.lx} y={e.ly} textAnchor={e.end ? 'end' : e.mid ? 'middle' : 'start'} className={`rc-port is-${edgeState(e, t)}`}>{e.label}</text>
        ))}
        {NODES.map((n) => {
          const st = nodeState(n, t);
          return (
            <g key={n.id} className={`rc-node is-${st}`} transform={`translate(${n.x} ${n.y})`}>
              <rect width={W} height={H} rx={7} />
              <circle cx={10} cy={13} r={3} fill={n.color} />
              <text x={17} y={16} className="rc-title">{n.title}</text>
              <text x={8} y={30} className="rc-type">{n.type}</text>
              <text x={8} y={40} className="rc-status">{st === 'done' ? n.done : st}</text>
              <circle cx={W - 9} cy={12} r={3} className="rc-light" />
            </g>
          );
        })}
      </svg>
      {extracting && (
        <div className="rc-swarm rc-rows">
          <div className="rc-swarm-head">
            <span className="rc-chip">For-each</span>
            <strong>Extract fields</strong>
            <span>{Math.min(3, ROWS.filter((r) => t >= r.at).length)} of 3</span>
          </div>
          <ul>
            {ROWS.map((r) => (
              <li key={r.id} className={`${t >= r.at ? 'is-on' : ''} ${r.hold ? 'is-hold' : ''}`}>
                <span>{r.id}</span><span>{r.vendor}</span><strong>{r.amount}</strong>
                {r.hold && <em>HOLD</em>}
              </li>
            ))}
          </ul>
          <p>The agent proposes the fields. It posts none of them.</p>
        </div>
      )}
      <div className="rc-legend">
        <span><i className="nq-light nq-light-queued" /> queued</span>
        <span><i className="nq-light nq-light-running" /> processing</span>
        <span><i className="nq-light nq-light-done" /> done</span>
        <span><i className="nq-light nq-light-skipped" /> branch not taken</span>
      </div>
    </div>
  );
}
